import React, { CSSProperties, ReactElement } from "react";
import colors from "@styles/variables/businessphonepricesVariables.module.scss";
import { formatPhoneNumber, getPhoneNumber } from "@lib/sharedUtils";
import style from "./style.module.scss";
import { ListingItem } from "@lib/shared/types";
import AffiliateOfferUrl from "../affiliateOfferUrl";
import { AffiliateOfferZone } from "@lib/shared/config";
import { useDomainContext } from "@hooks/useDomainContext";

export const getText = (
    text: { [key: string]: string } | undefined | null,
    locale: string,
) => {
    return text?.[locale] ?? text?.["en"] ?? "";
};

export default function OfferVertical({
    offer,
    zone,
    displayLinkAndPhoneNumber,
    customStyle,
    template = "default",
}: {
    offer: ListingItem;
    zone: AffiliateOfferZone;
    displayLinkAndPhoneNumber?: boolean;
    customStyle?: {
        fixedHeight?: boolean;
        wrapperStyle?: string;
        buttonBgColor?: string;
    };
    template?: "default" | "thankYou";
}): ReactElement {
    const phoneNumber = getPhoneNumber(offer);
    const { domain, category, locale } = useDomainContext();

    const callToActionTitle = getText(
        category?.config?.offerVerticalCallToActionTitle,
        locale,
    );
    const callToActionSubTitle = getText(
        category?.config?.offerVerticalCallToActionSubTitle,
        locale,
    );
    const buttonFallbackText = getText(
        category?.config?.offerVerticalButtonFallbackText,
        locale,
    );
    const freeEvaluationNote = getText(
        category?.config?.offerVerticalFreeEvaluationNote,
        locale,
    );

    const phoneNumberMessage = offer?.attributes.phoneNumberMessage
        ? offer?.attributes.phoneNumberMessage
        : offer?.hiddenAttributes.phoneNumberMessage
        ? offer?.hiddenAttributes.phoneNumberMessage
        : "";

    const outerSectionStyle = {
        "--buttonBackgroundColor": `${
            customStyle?.buttonBgColor ?? colors.autumnGlory
        }`,
    } as CSSProperties;

    const clickToCallBorder =
        typeof domain.config?.clickToCallBorder === "string"
            ? domain.config.clickToCallBorder
            : undefined;
    const clickToCallBtnBackground =
        typeof domain.config?.clickToCallBtnBackground === "string"
            ? domain.config.clickToCallBtnBackground
            : undefined;
    const clickToCallBtnForground =
        typeof domain.config?.clickToCallBtnForground === "string"
            ? domain.config.clickToCallBtnForground
            : undefined;
    const clickToCallBtnShadow =
        typeof domain.config?.clickToCallBtnShadow === "string"
            ? `rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, ${domain.config.clickToCallBtnShadow} 0px 10px 15px -3px, ${domain.config.clickToCallBtnShadow} 0px 4px 6px -4px `
            : undefined;

    return (
        <div
            className={`${style.container}  relative  mb-[30px] px-4 py-4   ${
                customStyle?.wrapperStyle ?? ""
            }`}
            style={outerSectionStyle}
        >
            <div className="flex flex-col flex-wrap items-center">
                <div
                    className={`w-40 mb-4 flex justify-center ${
                        customStyle?.fixedHeight ? "h-20" : ""
                    }`}
                    style={{
                        borderColor: colors.offerBorderColor,
                    }}
                >
                    {offer.logo && (
                        <img src={offer.logo.fullPath} alt={offer.title} />
                    )}
                </div>
                <div className="w-full">
                    <div className="w-full">
                        <h2 className={style["title"]}>
                            <span
                                style={{
                                    fontSize: "24px",
                                    fontWeight: "700",
                                    lineHeight: 1.2,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html:
                                        phoneNumberMessage ||
                                        callToActionTitle ||
                                        "Call Now & Discuss Options!",
                                }}
                            />
                        </h2>
                        <p className={style["secondTitle"]}>
                            {callToActionSubTitle ||
                                "Review offers and coverage eligibility instantly."}
                        </p>
                    </div>
                    <div className="order-3 flex flex-col mt-2 w-auto">
                        <AffiliateOfferUrl
                            zone={zone}
                            template={template}
                            className={`${
                                !displayLinkAndPhoneNumber ? style.link : ""
                            } ${style["linkPageBuilderStyle"]} ${
                                style["btnStyle"]
                            } flex justify-center rounded-5 text-center px-[10px] py-[10px] text-[15px]`}
                            offer={offer}
                            customHref={
                                phoneNumber
                                    ? `tel:${phoneNumber ?? ""}`
                                    : undefined
                            }
                            target="_blank"
                            style={{
                                borderRadius: "12px",
                                border: clickToCallBorder,
                                background: clickToCallBtnBackground,
                                color: clickToCallBtnForground,
                                boxShadow: clickToCallBtnShadow,
                            }}
                        >
                            {formatPhoneNumber(phoneNumber) ||
                                buttonFallbackText ||
                                "Let's Go"}
                        </AffiliateOfferUrl>
                        {displayLinkAndPhoneNumber && (
                            <AffiliateOfferUrl
                                zone={zone}
                                className={`text-left lg:text-center py-3 rounded-lg ${style["letGoStyle"]}`}
                                offer={offer}
                                target="_blank"
                            >
                                <span>{buttonFallbackText || "Let's Go"}</span>
                            </AffiliateOfferUrl>
                        )}
                    </div>
                    {formatPhoneNumber(phoneNumber) ? (
                        <div
                            style={{
                                fontSize: "12px",
                                color: "#666",
                                textAlign: "center",
                                marginTop: "15px",
                            }}
                        >
                            {freeEvaluationNote ||
                                "Free Evaluation • No Obligation"}
                        </div>
                    ) : (
                        <></>
                    )}
                </div>
            </div>
        </div>
    );
}
